import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

interface Prize {
  name?: string;
  value?: number;
}

interface LotteryEntry {
  _id: string;
  tagId: string;
  status: string;
  prize?: Prize;
  joinedAt?: string;
  drawnAt?: string;
  claimedAt?: string;
}

const LotteryResultPage: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState<LotteryEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entryId) {
      setError("No entry ID provided.");
      setLoading(false);
      return;
    }
    fetchEntry(entryId);
  }, [entryId]);

  const fetchEntry = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/consumer-lottery/entry/${id}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${
            localStorage.getItem("consumerToken") ||
            localStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            ""
          }`,
        },
      });

      const data = await res.json();
      console.log("📥 Entry response:", data);

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load entry result.");
      }

      const entryData = data.entry || data.data?.entry;
      if (!entryData) throw new Error("Invalid response from server");
      setEntry(entryData);
      setError(null);
    } catch (err: any) {
      console.error("❌ Lottery result fetch error:", err);
      setError(err.message || "Unable to load lottery result.");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    if (entryId) fetchEntry(entryId);
    else navigate("/consumer/my-entries");
  };

  const handleBackHome = () => navigate("/consumer");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-purple-600 rounded-full mx-auto mb-4"></div>
          <p className="text-purple-700 font-semibold text-lg">
            Loading your lottery result...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 p-4">
        <div className="bg-white shadow-xl rounded-2xl p-8 text-center max-w-sm w-full">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-red-500 mb-2">Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={handleRetry}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
          >
            Retry
          </button>
          <button
            onClick={handleBackHome}
            className="mt-3 w-full bg-gray-100 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md w-full">
          <div className="text-6xl mb-4">ℹ️</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">No Result Found</h2>
          <p className="text-gray-600 mb-6">
            We couldn't find a result for this entry ID.
          </p>
          <button
            onClick={handleBackHome}
            className="bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700"
          >
            Back Home
          </button>
        </div>
      </div>
    );
  }

  const isWinner = entry.status.toLowerCase() === "winner";
  const isClaimed = entry.status.toLowerCase() === "claimed";
  const isLoser =
    entry.status.toLowerCase() === "loser" ||
    entry.status.toLowerCase() === "not_winner";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-pink-900 text-white p-4">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-10 text-center border border-white/20">
        {isWinner && (
          <>
            <div className="text-8xl mb-4 animate-pulse-glow">🏆</div>
            <h1 className="text-4xl font-bold text-yellow-300 mb-2">WINNER!</h1>
            <p className="text-xl text-green-200 mb-6">
              Congratulations! You won{" "}
              <strong>{entry.prize?.name || "a prize"}</strong>
              {entry.prize?.value ? ` valued at $${entry.prize.value}` : ""} 🎁
            </p>
          </>
        )}

        {isLoser && (
          <>
            <div className="text-8xl mb-4">😔</div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Better Luck Next Time!
            </h1>
            <p className="text-xl text-purple-200 mb-6">
              You didn't win this time, but keep scanning authentic products for more chances!
            </p>
          </>
        )}

        {isClaimed && (
          <>
            <div className="text-8xl mb-4">✅</div>
            <h1 className="text-4xl font-bold text-blue-300 mb-2">
              Prize Already Claimed
            </h1>
            <p className="text-lg text-blue-100 mb-6">
              You've already claimed your prize. Thank you for participating!
            </p>
          </>
        )}

        <div className="bg-white/10 rounded-2xl p-6 mb-6 text-left text-sm space-y-2 border border-white/20">
          <p><strong>Tag ID:</strong> {entry.tagId}</p>
          <p><strong>Status:</strong> {entry.status}</p>
          {entry.prize && (
            <p>
              <strong>Prize:</strong> {entry.prize.name}{" "}
              {entry.prize.value ? `($${entry.prize.value})` : ""}
            </p>
          )}
          {entry.drawnAt && (
            <p><strong>Drawn At:</strong> {new Date(entry.drawnAt).toLocaleString()}</p>
          )}
          {entry.joinedAt && (
            <p><strong>Joined:</strong> {new Date(entry.joinedAt).toLocaleString()}</p>
          )}
        </div>

        {isWinner && (
          <button
            onClick={() => navigate(`/consumer/lottery-certificate/${entry._id}`)}
            className="w-full bg-green-600 py-3 rounded-xl hover:bg-green-700 text-white font-semibold transition-all mb-3"
          >
            🏅 View Lottery Certificate
          </button>
        )}

        <button
          onClick={() => navigate("/consumer/my-entries")}
          className="w-full bg-white/20 py-3 rounded-xl hover:bg-white/30 text-white font-semibold transition-all mb-3"
        >
          📋 View My Entries
        </button>
        <button
          onClick={handleBackHome}
          className="w-full bg-purple-600 py-3 rounded-xl hover:bg-purple-700 text-white font-semibold transition-all"
        >
          🔍 Scan Another Tag
        </button>
      </div>

      <style>{`
        @keyframes pulse-glow {
          0%,100%{box-shadow:0 0 20px rgba(255,215,0,.5)}
          50%{box-shadow:0 0 40px rgba(255,215,0,.8)}
        }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default LotteryResultPage;
